// components/TextInput.tsx
import { useFormContext } from 'react-hook-form';

import type { FormOptions, GeneralFormProps } from '@/types/general';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from '../ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

type Props<T extends string> = {
  options: FormOptions;
  value?: T;
  onChange?: (value: T) => void;
  helperText?: string;
} & Omit<GeneralFormProps, 'description'>;

function RHFShadcnSelect<T extends string>({
  name,
  options,
  placeholder,
  title,
  helperText,
  value: externalValue,
  onChange: externalOnChange,
  required,
  disabled,
}: Props<T>) {
  const { control } = useFormContext();

  const hasTitle = title !== undefined && title !== '';
  function buildFloatingLabel(): string | undefined {
    if (hasTitle === false) {
      return undefined;
    }
    if (required === true) {
      return `${title} *`;
    }
    return title;
  }
  const floatingLabel = buildFloatingLabel();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className="w-full">
          <Select
            disabled={disabled}
            required={required}
            data-testid={field.name}
            onValueChange={(value) => {
              if (externalOnChange !== null && externalOnChange !== undefined) {
                externalOnChange(value);
              }
              field.onChange(value);
            }}
            value={field.value || externalValue}
          >
            <FormControl>
              <SelectTrigger
                label={floatingLabel}
                aria-invalid={fieldState.error !== undefined ? true : undefined}
              >
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {options.map(item => (
                <SelectItem key={item.value} value={item.value}>
                  <p>{item.label}</p>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {helperText !== undefined && helperText !== '' && fieldState.error === undefined && (
            <FormDescription className="mt-1.5 type-body-s text-on-surface-variant">
              {helperText}
            </FormDescription>
          )}
          <FormMessage className="mt-1.5" />
        </FormItem>
      )}
    />
  );
}

export default RHFShadcnSelect;
